import { useEffect, useMemo, useState } from 'react';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { useNavigation } from '@react-navigation/native';
import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { resolveOrganizationRubroDisplayLabel } from '../../shell/session/domain/businessProfile';
import { BUSINESS_FUNCTION_CODES } from '../domain/businessFunctionsCatalog';
import {
  branchUsageFromEntitlements,
  userUsageFromEntitlements,
} from '../../../../../shared/plan-benefits';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { pickProductImage, isProductImageValidationError } from '../../catalog/infra-api';
import { optimizeStoreImage } from '../infra/optimizeStoreImage';
import {
  persistOrganizationLogo,
  removeOrganizationLogoStorage,
  uploadOrganizationLogo,
} from '../infra/organizationLogoRepo';
import { requireSessionOrganizationId } from '../../shell/session/sessionOrganization';

export function useOrganizationScreen() {
  const navigation = useNavigation<any>();
  const session = useAuthStore((state) => state.session);
  const branches = useSessionStore((state) => state.branches);
  const { entitlements, loading: entLoading, refresh: refreshEntitlements } = usePlanEntitlements();
  const businessProfile = useBusinessProfileStore((state) => state.profile);
  const canViewOrg = useModulePermission('Organization', 'view');
  const canEditOrg = useModulePermission('Organization', 'edit');
  const canChangeTypeAction = useModulePermission('Organization', 'change_type');
  const canCloseBusinessAction = useModulePermission('Organization', 'close_business');

  const [organizationNameInput, setOrganizationNameInput] = useState('');
  const [savingOrganizationName, setSavingOrganizationName] = useState(false);
  const [remoteOrganizationName, setRemoteOrganizationName] = useState<string | null>(null);
  const [organizationEditOpen, setOrganizationEditOpen] = useState(false);
  const [entitlementsRetrying, setEntitlementsRetrying] = useState(false);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState<string | null>(
    businessProfile?.organization_logo_url ?? null,
  );
  const [logoUploading, setLogoUploading] = useState(false);

  const activeModulesCount = useMemo(() => {
    const caps = new Set(businessProfile?.capabilities ?? []);
    return BUSINESS_FUNCTION_CODES.filter((code) => caps.has(code)).length;
  }, [businessProfile?.capabilities]);

  const totalModulesCount = BUSINESS_FUNCTION_CODES.length;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id || cancelled) return;
      const { getSessionOrganizationId } = await import('../../shell/session/sessionOrganization');
      const orgId = await getSessionOrganizationId();
      let query = supabase
        .from('employees')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true)
        .neq('auth_user_id', user.id);
      if (orgId) query = query.eq('organization_id', orgId);
      const { count, error } = await query;
      if (cancelled || error) return;
      setMemberCount(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, []);

  const entitlementsLoadMissing = !entLoading && !entitlements && Boolean(session);
  const isOwner = entitlements?.is_owner === true;
  const allowViewOrg = isOwner || canViewOrg;
  const allowEditOrg = isOwner || canEditOrg;
  // Rehabilitado: cambiar rubro con flujo Otros + ticket automático.
  const canChangeBusinessType = isOwner || canChangeTypeAction;
  const allowCloseBusiness = isOwner || canCloseBusinessAction;

  const organizationNameFromApi = useMemo(() => {
    const fromRemote = typeof remoteOrganizationName === 'string' ? remoteOrganizationName.trim() : '';
    if (fromRemote) return fromRemote;
    const fromEnt = typeof entitlements?.organization_name === 'string' ? entitlements.organization_name.trim() : '';
    if (fromEnt) return fromEnt;
    return null;
  }, [remoteOrganizationName, entitlements?.organization_name]);

  const organizationDisplayName = useMemo(() => {
    if (organizationNameFromApi) return organizationNameFromApi;
    if (entitlementsLoadMissing && branches.length > 0) return 'Datos de organización no disponibles';
    return 'Sin nombre cargado';
  }, [organizationNameFromApi, entitlementsLoadMissing, branches.length]);

  const planName = useMemo(() => {
    if (entitlements?.display_name) return entitlements.display_name;
    if (entLoading && !entitlements) return '…';
    if (entitlementsLoadMissing) return 'No disponible';
    return '—';
  }, [entitlements?.display_name, entLoading, entitlements, entitlementsLoadMissing]);

  const branchUsage = useMemo(
    () => (entitlements ? branchUsageFromEntitlements(entitlements) : null),
    [entitlements],
  );

  const userUsage = useMemo(
    () => (entitlements ? userUsageFromEntitlements(entitlements) : null),
    [entitlements],
  );

  useEffect(() => {
    const nextName = organizationNameFromApi ?? '';
    if (!organizationEditOpen) {
      setOrganizationNameInput(nextName);
    }
  }, [organizationEditOpen, organizationNameFromApi]);

  useEffect(() => {
    let cancelled = false;

    const loadOrganizationName = async () => {
      if (!entitlements?.organization_id) {
        setRemoteOrganizationName(null);
        return;
      }

      const fromEnt =
        typeof entitlements.organization_name === 'string' ? entitlements.organization_name.trim() : '';
      if (fromEnt) {
        setRemoteOrganizationName(fromEnt);
        return;
      }

      const { data, error } = await supabase
        .from('organizations')
        .select('name, logo_url')
        .eq('id', entitlements.organization_id)
        .maybeSingle();

      if (cancelled) return;
      if (error) {
        console.warn('[OrganizationScreen] loadOrganizationName', error.message);
        return;
      }

      setRemoteOrganizationName(typeof data?.name === 'string' ? data.name : null);
      const logo =
        typeof data?.logo_url === 'string' && data.logo_url.trim() ? data.logo_url.trim() : null;
      setOrganizationLogoUrl(logo);
    };

    void loadOrganizationName();

    return () => {
      cancelled = true;
    };
  }, [entitlements?.organization_id]);

  useEffect(() => {
    if (!businessProfile) return;
    setOrganizationLogoUrl(businessProfile.organization_logo_url ?? null);
  }, [businessProfile?.organization_logo_url, businessProfile]);

  const patchBusinessProfileLogo = (logoUrl: string | null) => {
    const profile = useBusinessProfileStore.getState().profile;
    if (!profile) return;
    useBusinessProfileStore.getState().setProfile({
      ...profile,
      organization_logo_url: logoUrl,
    });
  };

  const handleUploadOrganizationLogo = async () => {
    if (!allowEditOrg || logoUploading) return;
    if (!entitlements?.organization_id) {
      showInfo('Sin organización', 'No pudimos identificar la organización activa.');
      return;
    }
    try {
      setLogoUploading(true);
      const picked = await pickProductImage();
      if (!picked) return;
      const optimized = await optimizeStoreImage(picked.uri, 'logo');
      const orgId = await requireSessionOrganizationId();
      const publicUrl = await uploadOrganizationLogo({
        orgId,
        base64: optimized.base64,
        mimeType: optimized.mimeType,
      });
      const saved = await persistOrganizationLogo({
        logoUrl: publicUrl,
        focalX: 50,
        focalY: 50,
        zoom: 1,
      });
      setOrganizationLogoUrl(saved.logoUrl);
      patchBusinessProfileLogo(saved.logoUrl);
      showSuccess('Logo listo', 'Ya quedó para el negocio y las sucursales que lo heredan.');
    } catch (e: unknown) {
      if (isProductImageValidationError(e)) {
        showError('Imagen no válida', e.message);
      } else {
        presentUnexpectedFailure({
          module: 'settings',
          op: 'organization.upload_logo',
          error: e,
          userMessage: 'No se pudo subir el logo.',
        });
      }
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveOrganizationLogo = async () => {
    if (!allowEditOrg || logoUploading) return;
    try {
      setLogoUploading(true);
      const orgId = await requireSessionOrganizationId();
      await removeOrganizationLogoStorage(orgId);
      const saved = await persistOrganizationLogo({ logoUrl: null });
      setOrganizationLogoUrl(saved.logoUrl);
      patchBusinessProfileLogo(null);
      showSuccess('Logo quitado', 'Las sucursales que lo heredaban también lo perdieron.');
    } catch (e: unknown) {
      presentUnexpectedFailure({
        module: 'settings',
        op: 'organization.remove_logo',
        error: e,
        userMessage: 'No se pudo quitar el logo.',
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleEntitlementsRetry = () => {
    setEntitlementsRetrying(true);
    void refreshEntitlements()
      .catch(() => {})
      .finally(() => setEntitlementsRetrying(false));
  };

  const handleSaveOrganizationName = async () => {
    if (!entitlements?.organization_id) {
      showInfo('Sin organización', 'No pudimos identificar la organización activa.');
      return;
    }
    if (!allowEditOrg) {
      showInfo('Sin permiso', 'No tenés permiso para editar la organización.');
      return;
    }

    const nextName = organizationNameInput.trim();
    if (!nextName) {
      showError('Nombre requerido', 'Ingresá un nombre para la organización.');
      return;
    }

    try {
      setSavingOrganizationName(true);
      const { error } = await supabase.rpc('update_my_organization_name', {
        p_name: nextName,
      });
      if (error) throw error;
      setOrganizationNameInput(nextName);
      setRemoteOrganizationName(nextName);
      setOrganizationEditOpen(false);
      await refreshEntitlements();
      showSuccess('Guardado', 'Nombre actualizado.');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'organization.update_name', error: e, userMessage: 'No se pudo actualizar el nombre de la organización.' });
    } finally {
      setSavingOrganizationName(false);
    }
  };

  const openOrganizationEdit = () => {
    if (!allowEditOrg) return;
    setOrganizationNameInput(organizationNameFromApi ?? '');
    setOrganizationEditOpen(true);
  };

  const closeOrganizationEdit = () => {
    setOrganizationNameInput(organizationNameFromApi ?? '');
    setOrganizationEditOpen(false);
  };

  const openBilling = () => navigateInShell(navigation, 'Billing');
  const openChangeBusinessType = () => navigateInShell(navigation, 'ChangeBusinessType');
  const openBusinessFunctions = () => navigateInShell(navigation, 'BusinessFunctions');

  return {
    entLoading,
    entitlements,
    entitlementsLoadMissing,
    isOwner,
    canViewOrg: allowViewOrg,
    canEditOrg: allowEditOrg,
    canCloseBusiness: allowCloseBusiness,
    canChangeBusinessType,
    organizationDisplayName,
    organizationNameFromApi,
    organizationEditOpen,
    organizationNameInput,
    setOrganizationNameInput,
    openOrganizationEdit,
    closeOrganizationEdit,
    handleSaveOrganizationName,
    savingOrganizationName,
    entitlementsRetrying,
    handleEntitlementsRetry,
    businessTypeName: businessProfile
      ? resolveOrganizationRubroDisplayLabel(businessProfile)
      : null,
    branchUsage,
    userUsage,
    planName,
    showPlanKpi: (isOwner || allowViewOrg) && !entitlementsLoadMissing,
    planCodeGratis: entitlements?.plan_code === 'gratis',
    refreshEntitlements,
    openBilling,
    openChangeBusinessType,
    openBusinessFunctions: isOwner ? openBusinessFunctions : undefined,
    branchCount: branches.length,
    memberCount,
    activeModulesCount,
    totalModulesCount,
    organizationLogoUrl,
    logoUploading,
    handleUploadOrganizationLogo,
    handleRemoveOrganizationLogo,
  };
}
